/**
 * Heatwave Report Handler
 * Handles: Daily Report (PDF), Daily Report Status (Excel), MIS Reports (Excel)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

/**
 * Load logo image
 */
const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

/**
 * Format date to DD/MM/YYYY
 */
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
    return dateStr.replace(/-/g, '/');
  }
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateStr;
  }
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Generate IST filename
 */
const generateISTFilename = (prefix) => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
  return `${prefix}_${istDate}_${istTimeStr}`;
};

/**
 * Extract district name
 */
const extractDistrictName = (rawData, rows) => {
  let districtName = '[DISTRICT]';
  if (rawData?.district_name) {
    districtName = String(rawData.district_name).trim().toUpperCase();
    console.log('✅ District from rawData.district_name:', districtName);
  } else if (rows?.length > 0 && rows[0]?.district_name) {
    districtName = String(rows[0].district_name).trim().toUpperCase();
    console.log('✅ District from first row:', districtName);
  } else if (rows?.length > 0) {
    console.log('🔍 First row keys:', Object.keys(rows[0]));
  }
  return districtName;
};

/**
 * Safe numeric value handler
 */
const getSafeNumericValue = (value) => {
  return (value !== null && value !== undefined) ? String(value) : '';
};

/**
 * Safe string value handler
 */
const getSafeStringValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'NA';
  }
  return String(value).trim();
};

/**
 * Handle Heatwave Daily Report PDF Generation
 * ✅ FORMAT: PDF ONLY
 */
export const handleHeatwaveDailyReportPDF = async (message, format, rawData) => {
  console.log('🔥 Heatwave Daily Report PDF generation started');

  // ✅ UPDATED: Only PDF allowed for Daily Report
  if (format !== 'pdf') {
    console.warn('⚠️ Unsupported format for heatwave daily report:', format);
    alert('Heatwave Daily Report only supports PDF format');
    return;
  }

  try {
    const doc = new jsPDF('l', 'pt', 'a4');
    const reportDate = message.debug?.tool_result?.parameters?.date ||
                       message.debug?.parameters?.date ||
                       new Date().toISOString().split('T')[0];
    const displayDate = formatDateToDDMMYYYY(reportDate);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;

    // Load logo
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
      console.log('✅ Logo loaded successfully');
    } catch (e) {
      console.warn('⚠️ Logo load failed:', e.message);
    }

    // Add logo
    if (logoImg) {
      const logoWidth = 60;
      const logoHeight = 60;
      const logoX = centerX - logoWidth / 2;
      doc.addImage(logoImg, 'JPEG', logoX, 20, logoWidth, logoHeight);
      console.log('✅ Logo added to PDF');
    }

    // Header
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });
    doc.text(`Heat Wave Report (Dated on ${displayDate})`, centerX, 135, { align: 'center' });

    // Extract rows and district
    let rows = [];
    if (rawData?.dr_heat_wave && Array.isArray(rawData.dr_heat_wave)) {
      console.log('✅ Extracted dr_heat_wave array');
      rows = rawData.dr_heat_wave;

      // Merge cumulative data
      if (rawData.dr_heat_wave_Cumm && Array.isArray(rawData.dr_heat_wave_Cumm)) {
        console.log('🔗 Merging cumulative heatwave data');
        const cummMap = {};
        rawData.dr_heat_wave_Cumm.forEach(cummRow => {
          cummMap[cummRow.region_id] = cummRow;
        });

        rows = rows.map(row => {
          const cummData = cummMap[row.region_id];
          if (cummData) {
            return {
              ...row,
              no_of_deaths_cum: cummData.no_of_deaths_cum,
              no_of_dec_ex_gratia_pay_done_cum: cummData.no_of_dec_ex_gratia_pay_done_cum,
              no_of_persons_hospitalized_cum: cummData.no_of_persons_hospitalized_cum,
              no_of_persons_hospitalized_pay_done_cum: cummData.no_of_persons_hospitalized_pay_done_cum
            };
          }
          return row;
        });

        console.log('✅ Cumulative heatwave data merged');
      }
    } else if (Array.isArray(rawData)) {
      console.log('✅ rawData is already an array');
      rows = rawData;
    }

    const districtName = extractDistrictName(rawData, rows);

    // Table headers
    const tableHeaders = [
      'S.No.',
      'Block Name',
      'No. of Deaths (Today)',
      'No. of Deaths (Cum)',
      'No. of Deceased Ex-Gratia Payment Done (Today)',
      'No. of Deceased Ex-Gratia Payment Done (Cum)',
      'No. of Persons Hospitalized (Today)',
      'No. of Persons Hospitalized (Cum)',
      'No. of Hospitalized Payment Done (Today)',
      'No. of Hospitalized Payment Done (Cum)',
      'Remarks'
    ];

    // Filter and prepare table data
    const filteredRows = rows.filter(row => {
      return row && typeof row === 'object' && (
        row.block_name ||
        row.no_of_deaths_today ||
        row.no_of_dec_ex_gratia_pay_done_today ||
        row.no_of_persons_hospitalized_today ||
        row.remarks
      );
    });

    console.log(`📊 Filtered: ${filteredRows.length} rows`);

    if (filteredRows.length === 0) {
      alert('No block data found in report!');
      return;
    }

    const tableData = filteredRows.map((row, idx) => [
      String(idx + 1),
      getSafeStringValue(row.block_name),
      getSafeNumericValue(row.no_of_deaths_today),
      getSafeNumericValue(row.no_of_deaths_cum),
      getSafeNumericValue(row.no_of_dec_ex_gratia_pay_done_today),
      getSafeNumericValue(row.no_of_dec_ex_gratia_pay_done_cum),
      getSafeNumericValue(row.no_of_persons_hospitalized_today),
      getSafeNumericValue(row.no_of_persons_hospitalized_cum),
      getSafeNumericValue(row.no_of_persons_hospitalized_pay_done_today),
      getSafeNumericValue(row.no_of_persons_hospitalized_pay_done_cum),
      getSafeStringValue(row.remarks)
    ]);

    // Generate table
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 160,
      theme: 'grid',
      bodyStyles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fontStyle: 'normal',
        font: 'Helvetica',
        fontSize: 8
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'normal',
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center',
        font: 'Helvetica',
        fontSize: 9,
        cellPadding: 4
      },
      columnStyles: {
        0: { cellWidth: 30, halign: 'center' },
        1: { cellWidth: 80 },
        2: { cellWidth: 50, halign: 'center' },
        3: { cellWidth: 50, halign: 'center' },
        4: { cellWidth: 60, halign: 'center' },
        5: { cellWidth: 60, halign: 'center' },
        6: { cellWidth: 55, halign: 'center' },
        7: { cellWidth: 55, halign: 'center' },
        8: { cellWidth: 60, halign: 'center' },
        9: { cellWidth: 60, halign: 'center' },
        10: { cellWidth: 80 }
      },
      styles: {
        font: 'Helvetica',
        fontSize: 8,
        cellPadding: 3,
        halign: 'left',
        fontStyle: 'normal',
        overflow: 'linebreak'
      },
      margin: { top: 160 }
    });

    // Footer
    const footerY = pageHeight - 180;
    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.text('Letter No.: ___________', 50, footerY + 100);
    doc.text('Date: ___________', 50, footerY + 120);
    doc.text('Copy: ___________', 50, footerY + 140);
    doc.text('Signature of District Officers / ', 500, footerY + 80);
    doc.text('Additional District Magistrate (ADM)', 500, footerY + 100);
    doc.text('(Disaster Management) / Officer in Charge', 500, footerY + 120);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, 500, footerY + 150);

    const finalFilename = generateISTFilename('daily_heat_wave_report') + '.pdf';
    doc.save(finalFilename);
    console.log('✅✅✅ Heatwave PDF downloaded successfully!');

  } catch (error) {
    console.error('❌ Heatwave PDF generation failed:', error);
    console.error('Stack:', error.stack);
    alert(`PDF Generation Error: ${error.message}`);
  }
};

/**
 * Check if tool name is a heatwave daily report (PDF)
 * @param {string} toolName - Tool name from backend
 * @returns {boolean}
 */
export const isHeatwaveReport = (toolName) => {
  const heatwaveReports = [
    'heatwave_daily_report',
    'heatwavedailyreport'
  ];
  return heatwaveReports.includes(toolName?.toLowerCase());
};